import { Component, OnInit, OnDestroy, inject, ViewChild, ElementRef, HostListener, output } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { RouteInfo, RouteSearchService } from '../../../core/services/route-search.service';
import { NgIcon, provideIcons } from '@ng-icons/core';

import {
  heroHome,
  heroDocument,
  heroPaperAirplane,
  heroCalendar,
  heroCalendarDays,
  heroCreditCard,
  heroUsers,
  heroUserPlus,
  heroBuildingLibrary,
  heroUser,
  heroAtSymbol,
  heroLockClosed,
  heroDevicePhoneMobile,
  heroDocumentCheck,
  heroFaceSmile,
  heroGlobeAlt,
  heroMagnifyingGlass,
  heroArrowUp,
  heroXMark,
} from '@ng-icons/heroicons/outline';

@Component({
  selector: 'app-route-search',
  standalone: true,
  imports: [FormsModule, MatIconModule, TranslateModule, NgIcon],
  providers: [
    provideIcons({
      heroHome,
      heroDocument,
      heroPaperAirplane,
      heroCalendar,
      heroCalendarDays,
      heroCreditCard,
      heroUsers,
      heroUserPlus,
      heroBuildingLibrary,
      heroUser,
      heroAtSymbol,
      heroLockClosed,
      heroDevicePhoneMobile,
      heroDocumentCheck,
      heroFaceSmile,
      heroGlobeAlt,
      heroMagnifyingGlass,
      heroArrowUp,
      heroXMark,
    }),
  ],
  templateUrl: './route-search.component.html',
  styleUrls: ['./route-search.component.scss'],
})
export class RouteSearchComponent implements OnInit, OnDestroy {
  private routeSearchService = inject(RouteSearchService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  readonly routeSelected = output<void>();

  searchQuery = '';
  filteredRoutes: RouteInfo[] = [];
  isPanelOpen = false;
  highlightedIndex = -1;

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.searchQuery = '';
      this.filteredRoutes = this.routeSearchService.searchRoutes(this.searchQuery);
    });

    this.filteredRoutes = this.routeSearchService.getAllRoutes();

    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(query => {
      this.filteredRoutes = this.routeSearchService.searchRoutes(query);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchQueryChange(query: string): void {
    this.searchSubject.next(query);
  }

  onInputBlur() {
    setTimeout(() => {
      this.isPanelOpen = false;
      this.highlightedIndex = -1;
    }, 150);
  }

  onOptionSelected(route: RouteInfo) {
    console.log('Selected route:', route);
    this.searchQuery = '';
    this.filteredRoutes = [];
    this.isPanelOpen = false;
    this.highlightedIndex = -1;
    this.routeSearchService.navigateToRoute(route);
    this.routeSelected.emit();
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isPanelOpen || this.filteredRoutes.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredRoutes.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredRoutes.length) {
          this.onOptionSelected(this.filteredRoutes[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.isPanelOpen = false;
        this.highlightedIndex = -1;
        this.searchInput.nativeElement.blur();
        break;
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredRoutes = this.routeSearchService.getAllRoutes();
    this.highlightedIndex = -1;
    this.searchInput.nativeElement.focus();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    this.searchQuery = '';
    this.filteredRoutes = [];
    this.isPanelOpen = false;
    this.highlightedIndex = -1;
    this.searchInput.nativeElement.blur();
  }

  @HostListener('document:keydown.control.k', ['$event'])
  @HostListener('document:keydown.meta.k', ['$event'])
  onSearchHotkey(event: KeyboardEvent): void {
    event.preventDefault();
    this.searchInput.nativeElement.focus();
    this.isPanelOpen = true;
  }
}
